import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { WorkspaceThemeProvider, WorkspaceToaster } from "@/lib/workspaceTheme";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import PricingPage from "@/pages/PricingPage";
import TemplateMarketingPage from "@/pages/TemplateMarketingPage";
import DemoPage from "@/pages/DemoPage";
import LegalPage from "@/pages/LegalPage";
import AuthPage from "@/pages/AuthPage";
import AuthRecoveryPage from "@/pages/AuthRecoveryPage";
import CookieBanner from "@/components/CookieBanner";
import DashboardLayout from "@/pages/DashboardLayout";
import DashboardPage from "@/pages/DashboardPage";
import DealsPage from "@/pages/DealsPage";
import DealDetailPage from "@/pages/DealDetailPage";
import DealRoomsPage, { RoomInvitationPage } from "@/pages/DealRoomsPage";
import DealRoomPage from "@/pages/DealRoomPage";
import DealWizard from "@/pages/DealWizard";
import DraftingPage from "@/pages/DraftingPage";
import TemplatesPage from "@/pages/TemplatesPage";
import IntakeQueuePage from "@/pages/IntakeQueuePage";
import CompanyProfilePage from "@/pages/CompanyProfilePage";
import CounterpartiesPage from "@/pages/CounterpartiesPage";
import SettingsPage from "@/pages/SettingsPage";
import TasksPage from "@/pages/TasksPage";
import FilesPage from "@/pages/FilesPage";
import ReviewPage from "@/pages/ReviewPage";
import NotificationsPage from "@/pages/NotificationsPage";
import OrganizationPage from "@/pages/OrganizationPage";
import PublicSharePage from "@/pages/PublicSharePage";
import ExpertSupportPage from "@/pages/ExpertSupportPage";
import AdminExpertSupportPage from "@/pages/admin/AdminExpertSupportPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import BillingPage from "@/pages/BillingPage";
import AdminLayout, { AdminOverview, AdminStub } from "@/pages/admin/AdminLayout";
import AdminDocumentsPage from "@/pages/admin/AdminDocumentsPage";
import AdminMarketingPage from "@/pages/admin/AdminMarketingPage";
import MarketingAnalytics from "@/components/MarketingAnalytics";
import RecordingMode from "@/components/RecordingMode";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--td-text-2)] font-mono text-xs">Loading…</div>;
  if (!user) return <Navigate to="/signin" state={{ from: location }} replace />;
  return children;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <WorkspaceThemeProvider>
            <MarketingAnalytics />
            <RecordingMode />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/templates/:slug" element={<TemplateMarketingPage />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/privacy-policy" element={<LegalPage slugOverride="privacy" />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/signin" element={<AuthPage mode="signin" />} />
              <Route path="/signup" element={<AuthPage mode="signup" />} />
              <Route path="/forgot-password" element={<AuthRecoveryPage mode="forgot" />} />
              <Route path="/reset-password" element={<AuthRecoveryPage mode="reset" />} />
              <Route path="/verify-email" element={<AuthRecoveryPage mode="verify" />} />
              <Route path="/share/:token" element={<PublicSharePage />} />

              {/* Client Portal */}
              <Route path="/app" element={<Protected><DashboardLayout /></Protected>}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="deals" element={<DealsPage />} />
                <Route path="rooms" element={<DealRoomsPage />} />
                <Route path="rooms/join" element={<RoomInvitationPage />} />
                <Route path="rooms/:id" element={<DealRoomPage />} />
                <Route path="tasks" element={<TasksPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="deals/new" element={<DealWizard />} />
                <Route path="deals/:id" element={<DealDetailPage />} />
                <Route path="drafting" element={<DraftingPage />} />
                <Route path="review" element={<ReviewPage />} />
                <Route path="templates" element={<TemplatesPage />} />
                <Route path="files" element={<FilesPage />} />
                <Route path="counterparties" element={<CounterpartiesPage />} />
                <Route path="company" element={<CompanyProfilePage />} />
                <Route path="expert" element={<ExpertSupportPage />} />
                <Route path="intakes" element={<IntakeQueuePage />} />
                <Route path="billing" element={<BillingPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="organization" element={<OrganizationPage />} />
                <Route path="integrations" element={<IntegrationsPage />} />
              </Route>

              {/* Admin Portal — role-gated inside AdminLayout */}
              <Route path="/admin" element={<Protected><AdminLayout /></Protected>}>
                <Route index element={<AdminOverview />} />
                <Route path="orgs" element={<AdminStub title="Client Organisations" />} />
                <Route path="users" element={<AdminStub title="Users" />} />
                <Route path="deals" element={<AdminStub title="Deals" />} />
                <Route path="documents" element={<AdminDocumentsPage />} />
                <Route path="templates" element={<AdminStub title="Template Management" />} />
                <Route path="intakes" element={<AdminStub title="Intake Queue" subtitle="Client submissions from the marketing site alternative-request form." />} />
                <Route path="early" element={<AdminMarketingPage />} />
                <Route path="audit" element={<AdminStub title="Audit Logs" />} />
                <Route path="expert" element={<AdminExpertSupportPage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <WorkspaceToaster />
            <CookieBanner />
            </WorkspaceThemeProvider>
          </BrowserRouter>
        </div>
      </AuthProvider>
    </I18nProvider>
  );
}
